import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import SuperFetch from 'library/helpers/superFetch';
import WithHyperLink from 'library/hoc/withHyperLink';
import useManagePermittedRoles from 'library/hooks/useManagePermittedRoles';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { floorsActions } from 'reduxStore/floors/slice';
import { Loader, Notification } from 'zComponents/atoms';
import { Button, Card, Col, Dropdown, Flex, Row, Tag, Typography } from 'zui/Antd';
import DeleteFloorModal from '../DeleteFloorModal';

const { Text, Title } = Typography;

const FloorSummary = ({ data }) => {
	const { loadFloors } = floorsActions;
	const dispatch = useDispatch();
	const [loadings, setLoading] = useState(true);
	const [floorsList, setFloorsList] = useState([]);
	const { data: floorsData } = useSelector((state) => state.Floors);

	useEffect(() => {
		setLoading(true);
		if (data && Object.keys(data).length > 0) {
			setFloorsList(floorsData);
			setLoading(false);
		}
	}, [data, floorsData]);

	// HOC
	const BaseLink = WithHyperLink((a) => {
		return <Text>{a.children}</Text>;
	});

	const FloorWithLink = (props) => {
		return <BaseLink {...props} />;
	};

	const SiteWithLink = (props) => {
		return <BaseLink {...props} />;
	};

	const items =
		floorsList.length > 0 &&
		floorsList.map((floors) => {
			return {
				key: floors.name,
				label: (
					<FloorWithLink type='floor' dataID={floors.floorID}>
						{floors.name}
					</FloorWithLink>
				)
			};
		});

	const isPermittedUpdateFloor = useManagePermittedRoles('updateFloor', {
		orgID: [data.orgID],
		siteID: [data.siteID],
		floorID: [data.floorID]
	});
	const isPermittedDeleteFloor = useManagePermittedRoles('deleteFloor', {
		orgID: [data.orgID],
		siteID: [data.siteID]
	});

	const onEdit = (value, field) => {
		if (field === 'name' && data.name !== value) {
			if (!/^[a-zA-Z0-9 -/]+$/.test(value)) {
				Notification('error', 'Floor name can only contain letters, numbers and spaces.');
			} else if (value.length > 40) {
				Notification('error', 'Floor name can only be 40 characters long.');
			} else {
				SuperFetch.patch('/floors/' + data.floorID, { name: value })
					.then((res) => {
						if (res) {
							if (res.statusCode === 200) {
								dispatch(loadFloors());
								Notification('success', 'Floor Name Updated.');
							}
						}
						return res;
					})
					.catch(() => {
						Notification('error', 'Update Failed, Please try again.');
					});
			}
		}

		if (field === 'description' && data.description !== value) {
			if (!/^[a-zA-Z0-9 \n.;/,-]+$/.test(value)) {
				Notification('error', 'Floor description can only contain letters, numbers, ,/;. and spaces.');
			} else if (value.length > 320) {
				Notification('error', 'Floor description can only be 320 characters long.');
			} else {
				SuperFetch.patch('/floors/' + data.floorID, { description: value })
					.then((res) => {
						if (res?.statusCode === 200) {
							dispatch(loadFloors());
							Notification('success', 'Foor Description Updated.');
						} else {
							throw new Error('Update Failed, Please try again.');
						}
					})
					.catch(() => {
						Notification('error', 'Update Failed, Please try again.');
					});
			}
		}
		return;
	};

	return loadings ? (
		<Row gutter={0} justify='space-around'>
			<Loader />
		</Row>
	) : (
		<Card>
			<Row gutter={0} justify='start'>
				<Col lg={12} md={12} sm={24} xs={24} className='floorName'>
					<Title
						editable={
							isPermittedUpdateFloor
								? {
										tooltip: 'Edit Floor Name',
										onChange: (value) => onEdit(value, 'name'),
										icon: <InfoCircleOutlined className='outlinedIcon' style={{ color: '#4d4f5c' }} />,
										triggerType: ['text', 'icon'],
										enterIcon: null
								  }
								: false
						}
						level={3}
						style={{
							margin: 0
						}}
					>
						{data.name}
					</Title>
					<SiteWithLink type='site' dataID={data.siteID}>
						<Tag
							className='infoLabel'
							color='volcano'
							style={{ fontWeight: '600', fontSize: '11px', marginTop: '5px' }}
						>
							{data.siteName}
						</Tag>
					</SiteWithLink>
					<Text
						editable={
							isPermittedUpdateFloor
								? {
										tooltip: 'Click to edit Description',
										onChange: (value) => onEdit(value, 'description'),
										icon: <InfoCircleOutlined className='outlinedIcon' style={{ color: '#4d4f5c' }} />,
										triggerType: ['text', 'icon'],
										enterIcon: null
								  }
								: false
						}
					>
						{data.description}
					</Text>
				</Col>
				<Col lg={12} md={12} sm={24} xs={24}>
					<Flex justify='flex-end' gap={8} align='flex-start'>
						{floorsList.length > 0 ? (
							<Dropdown menu={{ items }} trigger={['click']}>
								<Button size='middle'>
									Switch Floors <DownOutlined />
								</Button>
							</Dropdown>
						) : null}
						{data.deletedDate ? (
							<Tag
								color='red'
								style={{
									padding: '5px 16px',
									display: 'flex',
									alignItems: 'center'
								}}
							>
								DELETED
							</Tag>
						) : (
							isPermittedDeleteFloor && <DeleteFloorModal data={data} />
						)}
					</Flex>
				</Col>
			</Row>
		</Card>
	);
};

export default FloorSummary;
